import React, { useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Sparkles, ShieldOff, Loader2, Copy, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function genRandomPassword(len = 12) {
  const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*";
  let out = "";
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  for (let i = 0; i < len; i++) out += charset[arr[i] % charset.length];
  return out;
}

/**
 * Yhdistetty modaali: salasanan nollaus + 2FA-resetointi.
 * Käytössä sekä Users että Licenses adminissa.
 *
 * Props:
 *  - open: boolean
 *  - onClose: ()
 *  - target: {kind: 'user' | 'license', id, email}
 *  - onPasswordShown?: ({email, password}) => void  // näytetään palautettu salasana modaalissa
 */
export default function ResetActionsModal({ open, onClose, target, onPasswordShown }) {
  const [mode, setMode] = useState("menu"); // menu | manual_pw | result_pw | result_2fa
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultPw, setResultPw] = useState(null); // {email, password}

  const reset = () => {
    setMode("menu");
    setCustom("");
    setResultPw(null);
    setLoading(false);
  };

  const closeAll = () => {
    reset();
    onClose?.();
  };

  const baseUrl = target
    ? (target.kind === "license" ? `/admin/licenses/${target.id}` : `/admin/users/${target.id}`)
    : "";

  const doResetPassword = async (newPassword) => {
    setLoading(true);
    try {
      const body = newPassword ? { new_password: newPassword } : {};
      const r = await api.post(`${baseUrl}/reset-password`, body);
      setResultPw({ email: r.data.email, password: r.data.new_password });
      setMode("result_pw");
      toast.success("Salasana päivitetty");
      onPasswordShown?.({ email: r.data.email, password: r.data.new_password });
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  const doReset2FA = async () => {
    if (!target) return;
    if (!window.confirm(
      `Nollataanko 2FA käyttäjältä ${target.email}? Käyttäjä joutuu enrolloimaan uudelleen seuraavalla kirjautumisella.`,
    )) return;
    setLoading(true);
    try {
      await api.post(`${baseUrl}/reset-2fa`);
      toast.success("2FA nollattu – käyttäjä enrolloi uudelleen kirjautuessaan");
      setMode("result_2fa");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  const copyAll = () => {
    if (!resultPw) return;
    navigator.clipboard.writeText(
      `Apps2Go Hub tunnukset\n\nSähköposti: ${resultPw.email}\nSalasana: ${resultPw.password}\n\nKirjaudu: ${window.location.origin}/login`,
    );
    toast.success("Tunnukset kopioitu leikepöydälle");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeAll()}>
      <DialogContent className="max-w-md" data-testid="reset-actions-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-400" /> Palautustyökalut
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-muted-foreground -mt-1">
          Kohde: <span className="font-mono">{target?.email}</span>
        </div>

        {mode === "menu" && (
          <div className="space-y-3 py-2">
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-3"
              onClick={() => doResetPassword(null)}
              disabled={loading}
              data-testid="reset-generate"
            >
              <Sparkles className="w-4 h-4 text-amber-400" />
              <div className="text-left">
                <p className="font-medium">Generoi satunnainen salasana</p>
                <p className="text-[11px] text-muted-foreground">Näyttää tunnukset kerran</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-3"
              onClick={() => setMode("manual_pw")}
              disabled={loading}
              data-testid="reset-manual"
            >
              <KeyRound className="w-4 h-4 text-amber-400" />
              <div className="text-left">
                <p className="font-medium">Syötä salasana käsin</p>
                <p className="text-[11px] text-muted-foreground">Voit antaa esim. tilapäisen koodin</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-3 border-red-500/30"
              onClick={doReset2FA}
              disabled={loading}
              data-testid="reset-2fa"
            >
              <ShieldOff className="w-4 h-4 text-red-400" />
              <div className="text-left">
                <p className="font-medium text-red-200">Nollaa 2FA</p>
                <p className="text-[11px] text-muted-foreground">
                  Käyttäjä joutuu skannaamaan uuden QR-koodin seuraavalla kirjautumisella
                </p>
              </div>
            </Button>
          </div>
        )}

        {mode === "manual_pw" && (
          <div className="space-y-3 py-2">
            <Label className="text-xs">Uusi salasana</Label>
            <div className="flex gap-2">
              <Input
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Vähintään 6 merkkiä"
                className="font-mono"
                data-testid="reset-custom-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setCustom(genRandomPassword(12))}
                title="Generoi"
              >
                <Sparkles className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Salasanaa ei tallenneta selväkielisenä – välitä se käyttäjälle nyt.
            </p>
            <div className="flex gap-2 pt-1">
              <Button variant="ghost" onClick={() => setMode("menu")} className="flex-1">
                Takaisin
              </Button>
              <Button
                onClick={() => custom.length >= 6 ? doResetPassword(custom) : toast.error("Vähintään 6 merkkiä")}
                disabled={loading}
                className="flex-1 gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900"
                data-testid="reset-save"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                Tallenna salasana
              </Button>
            </div>
          </div>
        )}

        {mode === "result_pw" && resultPw && (
          <div className="space-y-3 py-2">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex gap-2 text-xs text-amber-200">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                <strong>Tärkeää:</strong> Tämä salasana näytetään vain kerran. Tallenna tai välitä se käyttäjälle nyt.
              </p>
            </div>
            <div className="rounded-lg bg-zinc-900/60 p-4 space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Sähköposti</p>
              <p className="font-mono text-sm">{resultPw.email}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground pt-2">Salasana</p>
              <p className="font-mono text-lg font-bold tracking-wider text-amber-300" data-testid="reset-pw-result">
                {resultPw.password}
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={copyAll} className="flex-1 gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900">
                <Copy className="w-4 h-4" /> Kopioi tunnukset
              </Button>
              <Button variant="outline" onClick={closeAll}>
                Sulje
              </Button>
            </div>
          </div>
        )}

        {mode === "result_2fa" && (
          <div className="space-y-3 py-2">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex gap-2 text-xs text-emerald-200">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                <strong>2FA nollattu.</strong> Käyttäjä ohjataan automaattisesti uudelleen-enrollointiin
                kun hän kirjautuu seuraavan kerran. Aiemmat varakoodit ja Authenticator-yhteys on poistettu.
              </p>
            </div>
            <Button onClick={closeAll} className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-900">
              Sulje
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
